import { useMemo, useCallback } from 'react';
// The sketch canvas is a single unsaved item rather than a Map entry, so its
// row id is fixed.
export const SKETCH_ITEM_ID = 'sketch';
export function useUnsavedItemsFacade(tools) {
    const { placemarksTool, measureTool, pathsTool, polygonTool, contourHighlight, streamHighlight, labelsTool, sketchTool } = tools;
    const unsavedItems = useMemo(() => {
        var _a;
        const items = [];
        placemarksTool.placemarks.forEach((p, id) => {
            items.push({ id, name: p.name, type: 'placemark', color: p.color });
        });
        measureTool.measurements.forEach((m, id) => {
            items.push({ id, name: m.name, type: 'measurement', color: m.color });
        });
        (_a = measureTool.circles) === null || _a === void 0 ? void 0 : _a.forEach((c, id) => {
            items.push({ id, name: c.name, type: 'measurement', color: c.color });
        });
        pathsTool.paths.forEach((p, id) => {
            items.push({ id, name: p.name, type: 'path', color: p.color });
        });
        polygonTool.polygons.forEach((p, id) => {
            items.push({ id, name: p.name, type: 'polygon', color: p.color });
        });
        labelsTool.labels.forEach((l, id) => {
            items.push({ id, name: l.name || 'Label', type: 'label', color: l.color });
        });
        contourHighlight.highlightedContours.forEach((highlight, id) => {
            items.push({
                id,
                name: highlight.name,
                type: 'contour_highlight',
                color: highlight.highlightColor.toCssColorString(),
            });
        });
        const streamGroups = streamHighlight.getAllContiguousGroups();
        streamGroups.forEach(({ groupId, entityIds, highlight }) => {
            const segmentCount = entityIds.size;
            const baseName = highlight.name;
            items.push({
                id: groupId,
                name: segmentCount > 1 && !baseName.includes('segment')
                    ? `${baseName} (${segmentCount} segments)`
                    : baseName,
                type: 'stream_highlight',
                color: highlight.highlightColor.toCssColorString(),
            });
        });
        if (sketchTool.strokeCount > 0) {
            items.push({
                id: SKETCH_ITEM_ID,
                name: `Sketch (${sketchTool.strokeCount} stroke${sketchTool.strokeCount === 1 ? '' : 's'})`,
                type: 'sketch',
                color: sketchTool.canvasColor,
            });
        }
        return items;
    }, [placemarksTool.placemarks, measureTool.measurements, measureTool.circles, pathsTool.paths, polygonTool.polygons, labelsTool.labels, contourHighlight.highlightedContours, streamHighlight, sketchTool.strokeCount, sketchTool.canvasColor]);
    const handleDeleteUnsavedItem = useCallback((id, type) => {
        var _a, _b;
        switch (type) {
            case 'measurement':
                if ((_a = measureTool.circles) === null || _a === void 0 ? void 0 : _a.has(id)) {
                    (_b = measureTool.removeCircle) === null || _b === void 0 ? void 0 : _b.call(measureTool, id);
                }
                else {
                    measureTool.removeMeasurement(id);
                }
                break;
            case 'path':
                pathsTool.removePath(id);
                break;
            case 'placemark':
                placemarksTool.removePlacemark(id);
                break;
            case 'polygon':
                polygonTool.removePolygon(id);
                break;
            case 'label':
                labelsTool.removeLabel(id);
                break;
            case 'contour_highlight':
                contourHighlight.unhighlightContour(id);
                break;
            case 'stream_highlight':
                if (id.startsWith('stream-group-')) {
                    const firstEntityId = id.replace('stream-group-', '');
                    const groupEntityIds = streamHighlight.findContiguousHighlighted(firstEntityId);
                    groupEntityIds.forEach(entityId => streamHighlight.unhighlightStream(entityId));
                }
                else {
                    streamHighlight.unhighlightStream(id);
                }
                break;
            case 'sketch':
                sketchTool.resetCanvas();
                break;
        }
    }, [measureTool, pathsTool, placemarksTool, polygonTool, labelsTool, contourHighlight, streamHighlight, sketchTool]);
    const handleToggleUnsavedVisibility = useCallback((id, type) => {
        switch (type) {
            case 'placemark':
                placemarksTool.toggleItemVisibility(id);
                break;
            case 'measurement':
                measureTool.toggleItemVisibility(id);
                break;
            case 'path':
                pathsTool.toggleItemVisibility(id);
                break;
            case 'polygon':
                polygonTool.toggleItemVisibility(id);
                break;
            case 'label':
                labelsTool.toggleItemVisibility(id);
                break;
            case 'contour_highlight':
                contourHighlight.toggleItemVisibility(id);
                break;
            case 'stream_highlight':
                streamHighlight.toggleGroupVisibility(id);
                break;
            case 'sketch':
                sketchTool.toggleVisibility();
                break;
        }
    }, [placemarksTool, measureTool, pathsTool, polygonTool, labelsTool, contourHighlight, streamHighlight, sketchTool]);
    const handleSetUnsavedOpacity = useCallback((id, type, opacity) => {
        switch (type) {
            case 'placemark':
                placemarksTool.updatePlacemarkOpacity(id, opacity);
                break;
            case 'measurement':
                measureTool.updateMeasurementOpacity(id, opacity);
                break;
            case 'path':
                pathsTool.updatePathOpacity(id, opacity);
                break;
            case 'polygon':
                polygonTool.updatePolygonOpacity(id, opacity);
                break;
            case 'label':
                labelsTool.updateLabelOpacity(id, opacity);
                break;
            case 'contour_highlight':
                contourHighlight.updateContourOpacity(id, opacity);
                break;
            case 'stream_highlight':
                streamHighlight.updateGroupOpacity(id, opacity);
                break;
            case 'sketch':
                sketchTool.setOpacity(opacity);
                break;
        }
    }, [placemarksTool, measureTool, pathsTool, polygonTool, labelsTool, contourHighlight, streamHighlight, sketchTool]);
    const handleIsUnsavedVisible = useCallback((id, type) => {
        switch (type) {
            case 'placemark': return placemarksTool.isItemVisible(id);
            case 'measurement': return measureTool.isItemVisible(id);
            case 'path': return pathsTool.isItemVisible(id);
            case 'polygon': return polygonTool.isItemVisible(id);
            case 'label': return labelsTool.isItemVisible(id);
            case 'contour_highlight': return contourHighlight.isItemVisible(id);
            case 'stream_highlight': return streamHighlight.isGroupVisible(id);
            case 'sketch': return sketchTool.isVisible;
            default: return true;
        }
    }, [placemarksTool, measureTool, pathsTool, polygonTool, labelsTool, contourHighlight, streamHighlight, sketchTool]);
    const handleGetUnsavedOpacity = useCallback((id, type) => {
        switch (type) {
            case 'placemark': return placemarksTool.getItemOpacity(id);
            case 'measurement': return measureTool.getItemOpacity(id);
            case 'path': return pathsTool.getItemOpacity(id);
            case 'polygon': return polygonTool.getItemOpacity(id);
            case 'label': return labelsTool.getItemOpacity(id);
            case 'contour_highlight': return contourHighlight.getItemOpacity(id);
            case 'stream_highlight': return streamHighlight.getGroupOpacity(id);
            case 'sketch': return sketchTool.opacity;
            default: return 1;
        }
    }, [placemarksTool, measureTool, pathsTool, polygonTool, labelsTool, contourHighlight, streamHighlight, sketchTool]);
    return {
        unsavedItems,
        handleDeleteUnsavedItem,
        handleToggleUnsavedVisibility,
        handleSetUnsavedOpacity,
        handleIsUnsavedVisible,
        handleGetUnsavedOpacity,
    };
}
